import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { media } from '@atlaskit/primitives/responsive';

import { wacToken } from '@/libraries/design/foundations';

import { messages } from './feedback-buttons.messages';

/**
 * Vote state for the FeedbackButtons component.
 *
 * `null` — user has not yet voted (both buttons enabled and unfilled)
 * `"up"` — user upvoted (thumbs-up highlighted, both buttons disabled)
 * `"down"` — user downvoted (thumbs-down highlighted, both buttons disabled)
 */
export type FeedbackVote = 'up' | 'down' | null;

export type FeedbackButtonsProps = {
	/**
	 * Current vote state. The corresponding thumb is highlighted when set.
	 */
	vote: FeedbackVote;
	/**
	 * Called when the user clicks a thumb button. Parent is responsible for
	 * updating `vote` and firing any analytics/persistence events.
	 *
	 * The vote is toggleable: clicking the other thumb switches the vote,
	 * and clicking the currently-selected thumb again clears it (emits
	 * `null`). Parents should therefore accept the full `FeedbackVote`
	 * union, including `null`.
	 */
	onVote: (vote: FeedbackVote) => void;
	/**
	 * Optional override for the leading question. Defaults to the intl
	 * "Did you find this page helpful?" message.
	 */
	label?: string;
	/**
	 * Optional data-testid override. Defaults to "ubp-feedback-buttons" to
	 * distinguish this q4 presentational component from the legacy
	 * "feedback-buttons" testid used elsewhere in wac-web.
	 */
	testId?: string;
};

/**
 * Presentational feedback buttons used on the Estimate (step 3) and Summary
 * (step 5) screens of the Q4 UBP calculator.
 *
 * This component is intentionally "dumb":
 *   - It does not read from or write to any context.
 *   - It does not fire analytics events.
 *   - It does not persist the vote anywhere.
 *
 * All those concerns live in the parent/container. The parent decides what
 * happens when the user clicks a thumb, and passes the resulting `vote` back
 * in as a prop so this component can render the correct visual state.
 *
 * Accessibility:
 *   - Each button has an `aria-label` describing the vote it represents.
 *   - Buttons expose `aria-pressed` so screen readers announce the toggle
 *     state.
 *   - After voting, buttons are `disabled` + `aria-disabled="true"` so the
 *     user can't double-vote and screen readers convey why the buttons no
 *     longer respond. A visually-hidden confirmation message is announced via
 *     `role="status"` so non-sighted users know the vote was captured.
 *   - The visible thumb icon carries no text (`label=""`) because the button's
 *     `aria-label` already conveys purpose — avoids double announcements.
 */
export const FeedbackButtons = ({
	vote,
	onVote,
	label,
	testId = 'ubp-feedback-buttons',
}: FeedbackButtonsProps) => {
	const intl = useIntl();
	const hasVoted = vote !== null;

	const handleClick = (nextVote: 'up' | 'down') => {
		// The user can freely switch between thumbs up and thumbs down, or
		// click the currently-selected thumb again to clear their vote.
		// (Previously this early-returned once a vote was cast, which locked
		// the user into their first choice — corrected so votes are toggleable.)
		onVote(vote === nextVote ? null : nextVote);
	};

	const resolvedLabel = label ?? intl.formatMessage(messages.label);

	return (
		<div css={containerStyles} data-testid={testId}>
			<span css={labelStyles}>{resolvedLabel}</span>
			<div css={buttonRowStyles}>
				<button
					type="button"
					css={[iconButtonStyles, vote === 'up' && iconButtonActiveStyles]}
					onClick={() => handleClick('up')}
					aria-label={intl.formatMessage(messages.thumbsUpAriaLabel)}
					aria-pressed={vote === 'up'}
					data-testid={`${testId}-thumbs-up`}
				>
					<ThumbsUpIcon label="" />
				</button>
				<button
					type="button"
					css={[iconButtonStyles, vote === 'down' && iconButtonActiveStyles]}
					onClick={() => handleClick('down')}
					aria-label={intl.formatMessage(messages.thumbsDownAriaLabel)}
					aria-pressed={vote === 'down'}
					data-testid={`${testId}-thumbs-down`}
				>
					<ThumbsDownIcon label="" />
				</button>
			</div>
			{/*
			 * Visually-hidden live region so screen readers announce the vote
			 * confirmation. Rendered only after a vote is cast to avoid
			 * announcing an empty region on mount.
			 */}
			{hasVoted ? (
				<span css={visuallyHiddenStyles} role="status">
					{intl.formatMessage(messages.thanksAfterVote)}
				</span>
			) : null}
		</div>
	);
};

// Left-aligned across all viewports so prompt + thumbs sit at the
// same x-origin as the surrounding content grid.
//   - Mobile (< 768px): column layout — prompt on top, thumbs below.
//   - Tablet / desktop: row layout — prompt + thumbs on same line.
const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	justifyContent: 'flex-start',
	gap: '12px',
	fontFamily: wacToken('font.charlie.text'),
	[media.above.md]: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '20px',
	},
});

const labelStyles = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	color: wacToken('color.primary.black'),
	whiteSpace: 'nowrap',
	margin: 0,
});

const buttonRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
});

const iconButtonStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: 'none',
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	padding: '12px',
	cursor: 'pointer',
	borderRadius: '8px',
	color: wacToken('color.primary.black'),
	transition: 'opacity 0.15s ease',
	':hover': {
		opacity: 0.7,
	},
	':focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
	':disabled': {
		cursor: 'initial',
		':hover': {
			opacity: 1,
		},
	},
});

const iconButtonActiveStyles = css({
	color: wacToken('color.primary.blue'),
});

// Standard visually-hidden pattern — keeps the confirmation available to
// screen readers without occupying visual space or shifting the layout.
const visuallyHiddenStyles = css({
	position: 'absolute',
	width: '1px',
	height: '1px',
	padding: 0,
	margin: '-1px',
	overflow: 'hidden',
	clip: 'rect(0, 0, 0, 0)',
	whiteSpace: 'nowrap',
	border: 0,
});
